import type {
  DocBuildingGraphPredicateLiteral,
  DocBuildingGraphPredicateFilterNode,
  DocBuildingGraphFilterGraph,
  DocBuildingGraphFilterNode,
  DocBuildingGraphSubfilterValueSource,
  PredicateOp,
} from "../walls2/wallPlaneFilters";
import type { FilterEntitySourceId } from "./entitySource";
import type { PropertyPath } from "./propertyCatalog";

type FilterLiteralValue = DocBuildingGraphPredicateLiteral;

export interface BuiltDocFilter<S extends FilterEntitySourceId> {
  readonly targetType: S;
  readonly filterGraphId: string;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
}

export interface BuiltSubfilterValueSource<S extends FilterEntitySourceId> {
  readonly sourceType: S;
  readonly selectPaths: readonly string[];
  readonly filterGraphId?: string;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
}

type FilterInput<S extends FilterEntitySourceId> = BuiltDocFilter<S> | BuiltFilterBuilder<S>;

let nodeCounter = 0;
let graphCounter = 0;

function nextNodeId(): string {
  nodeCounter += 1;
  return `node_${nodeCounter}`;
}

function nextGraphId(): string {
  graphCounter += 1;
  return `graph_${graphCounter}`;
}

function createSingleNodeFilter<S extends FilterEntitySourceId>(
  sourceType: S,
  node: DocBuildingGraphFilterNode,
): BuiltDocFilter<S> {
  const nodeId = nextNodeId();
  const filterGraphId = nextGraphId();
  return {
    targetType: sourceType,
    filterGraphId,
    filterGraphs: {
      [filterGraphId]: {
        rootNodeId: nodeId,
        nodes: {
          [nodeId]: node,
        },
      },
    },
  };
}

function cloneGraph(graph: DocBuildingGraphFilterGraph): DocBuildingGraphFilterGraph {
  return {
    rootNodeId: graph.rootNodeId,
    nodes: { ...graph.nodes },
  };
}

function requireGraph(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
): DocBuildingGraphFilterGraph {
  const graph = filterGraphs[graphId];
  if (!graph) {
    throw new Error(`Missing filter graph "${graphId}"`);
  }
  return graph;
}

function resolveFilter<S extends FilterEntitySourceId>(filter: FilterInput<S>): BuiltDocFilter<S> {
  if (filter instanceof BuiltFilterBuilder) {
    return filter.build();
  }
  return filter;
}

function assertSameSourceType<S extends FilterEntitySourceId>(sourceType: S, filter: BuiltDocFilter<S>): void {
  if (filter.targetType !== sourceType) {
    throw new Error(`Filter targetType "${filter.targetType}" does not match sourceType "${sourceType}"`);
  }
}

function stripSubfilterValueSource<S extends FilterEntitySourceId>(
  valueSource: BuiltSubfilterValueSource<S>,
): DocBuildingGraphSubfilterValueSource {
  return {
    sourceType: valueSource.sourceType,
    selectPaths: [...valueSource.selectPaths],
    filterGraphId: valueSource.filterGraphId,
  };
}

function predicateFilter<S extends FilterEntitySourceId>(
  sourceType: S,
  op: PredicateOp,
  path: PropertyPath<S>,
  values?: readonly FilterLiteralValue[],
  valueSource?: BuiltSubfilterValueSource<FilterEntitySourceId>,
): BuiltDocFilter<S> {
  const node: DocBuildingGraphPredicateFilterNode = {
    filterType: "PREDICATE",
    targetType: sourceType,
    op,
    path,
    values,
    valueSource: valueSource ? stripSubfilterValueSource(valueSource) : undefined,
  };
  const filter = createSingleNodeFilter(sourceType, node);
  return {
    targetType: sourceType,
    filterGraphId: filter.filterGraphId,
    filterGraphs: {
      ...filter.filterGraphs,
      ...(valueSource?.filterGraphs ?? {}),
    },
  };
}

function withNewRootNode<S extends FilterEntitySourceId>(
  filter: BuiltDocFilter<S>,
  node: DocBuildingGraphFilterNode,
): BuiltDocFilter<S> {
  const rootGraph = requireGraph(filter.filterGraphs, filter.filterGraphId);
  const nodeId = nextNodeId();
  const updatedRootGraph: DocBuildingGraphFilterGraph = {
    rootNodeId: nodeId,
    nodes: {
      ...rootGraph.nodes,
      [nodeId]: node,
    },
  };
  return {
    targetType: filter.targetType,
    filterGraphId: filter.filterGraphId,
    filterGraphs: {
      ...filter.filterGraphs,
      [filter.filterGraphId]: updatedRootGraph,
    },
  };
}

function materializeFilterRootInGraph<S extends FilterEntitySourceId>(
  filter: BuiltDocFilter<S>,
  targetGraphId: string,
  targetNodes: Record<string, DocBuildingGraphFilterNode>,
): string {
  const sourceRootGraph = requireGraph(filter.filterGraphs, filter.filterGraphId);
  if (filter.filterGraphId === targetGraphId) {
    return sourceRootGraph.rootNodeId;
  }
  for (const [nodeId, node] of Object.entries(sourceRootGraph.nodes)) {
    targetNodes[nodeId] = node;
  }
  return sourceRootGraph.rootNodeId;
}

function combineWithConnective<S extends FilterEntitySourceId>(
  sourceType: S,
  connective: "AND" | "OR",
  first: BuiltDocFilter<S>,
  others: readonly BuiltDocFilter<S>[],
): BuiltDocFilter<S> {
  const all = [first, ...others];
  for (const filter of all) {
    assertSameSourceType(sourceType, filter);
  }

  const mergedFilterGraphs: Record<string, DocBuildingGraphFilterGraph> = {};
  for (const filter of all) {
    Object.assign(mergedFilterGraphs, filter.filterGraphs);
  }

  const rootGraph = cloneGraph(requireGraph(mergedFilterGraphs, first.filterGraphId));
  const childNodeIds = all.map((filter) => materializeFilterRootInGraph(filter, first.filterGraphId, rootGraph.nodes));

  const rootNodeId = nextNodeId();
  const updatedRootGraph: DocBuildingGraphFilterGraph = {
    rootNodeId,
    nodes: {
      ...rootGraph.nodes,
      [rootNodeId]: {
        filterType: connective,
        targetType: sourceType,
        childrenNodeIds: childNodeIds,
      },
    },
  };
  mergedFilterGraphs[first.filterGraphId] = updatedRootGraph;

  return {
    targetType: sourceType,
    filterGraphId: first.filterGraphId,
    filterGraphs: mergedFilterGraphs,
  };
}

function combineChildrenWithConnective<S extends FilterEntitySourceId>(
  sourceType: S,
  connective: "AND" | "OR",
  children: readonly BuiltDocFilter<S>[],
): BuiltDocFilter<S> {
  if (children.length === 0) {
    return createSingleNodeFilter(sourceType, {
      filterType: connective,
      targetType: sourceType,
      childrenNodeIds: [],
    });
  }
  const [first, ...rest] = children;
  return combineWithConnective(sourceType, connective, first, rest);
}

export class BuiltFilterBuilder<S extends FilterEntitySourceId> {
  constructor(
    private readonly sourceType: S,
    private readonly filter: BuiltDocFilter<S>,
  ) {}

  eq(path: PropertyPath<S>, value: FilterLiteralValue): BuiltFilterBuilder<S> {
    return this.and(new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "EQ", path, [value])));
  }

  neq(path: PropertyPath<S>, value: FilterLiteralValue): BuiltFilterBuilder<S> {
    return this.and(new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "NEQ", path, [value])));
  }

  in(path: PropertyPath<S>, values: readonly FilterLiteralValue[]): BuiltFilterBuilder<S> {
    return this.and(new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "IN", path, values)));
  }

  inFrom<VS extends FilterEntitySourceId>(
    path: PropertyPath<S>,
    valueSource: BuiltSubfilterValueSource<VS>,
  ): BuiltFilterBuilder<S> {
    return this.and(
      new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "IN", path, undefined, valueSource)),
    );
  }

  contains(path: PropertyPath<S>, value: FilterLiteralValue): BuiltFilterBuilder<S> {
    return this.and(
      new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "CONTAINS", path, [value])),
    );
  }

  containsFrom(
    path: PropertyPath<S>,
    valueSource: BuiltSubfilterValueSource<FilterEntitySourceId>,
  ): BuiltFilterBuilder<S> {
    return this.and(
      new BuiltFilterBuilder(
        this.sourceType,
        predicateFilter(this.sourceType, "CONTAINS", path, undefined, valueSource),
      ),
    );
  }

  intersects(path: PropertyPath<S>, values: readonly FilterLiteralValue[]): BuiltFilterBuilder<S> {
    return this.and(
      new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "INTERSECTS", path, values)),
    );
  }

  intersectsFrom(
    path: PropertyPath<S>,
    valueSource: BuiltSubfilterValueSource<FilterEntitySourceId>,
  ): BuiltFilterBuilder<S> {
    return this.and(
      new BuiltFilterBuilder(
        this.sourceType,
        predicateFilter(this.sourceType, "INTERSECTS", path, undefined, valueSource),
      ),
    );
  }

  exists(path: PropertyPath<S>): BuiltFilterBuilder<S> {
    return this.and(new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "EXISTS", path)));
  }

  and(...children: readonly FilterInput<S>[]): BuiltFilterBuilder<S> {
    const resolvedChildren = children.map((child) => resolveFilter(child));
    return new BuiltFilterBuilder(
      this.sourceType,
      combineWithConnective(this.sourceType, "AND", this.filter, resolvedChildren),
    );
  }

  or(...children: readonly FilterInput<S>[]): BuiltFilterBuilder<S> {
    const resolvedChildren = children.map((child) => resolveFilter(child));
    return new BuiltFilterBuilder(
      this.sourceType,
      combineWithConnective(this.sourceType, "OR", this.filter, resolvedChildren),
    );
  }

  not(): BuiltFilterBuilder<S>;
  not(child: FilterInput<S> | ((source: SourceFilterBuilder<S>) => FilterInput<S>)): BuiltFilterBuilder<S>;
  not(child?: FilterInput<S> | ((source: SourceFilterBuilder<S>) => FilterInput<S>)): BuiltFilterBuilder<S> {
    if (child !== undefined) {
      const negatedChild = typeof child === "function" ? child(new SourceFilterBuilder(this.sourceType)) : child;
      const childFilter = resolveFilter(negatedChild);
      const negatedChildFilter = withNewRootNode(childFilter, {
        filterType: "NOT",
        targetType: this.sourceType,
        childNodeId: requireGraph(childFilter.filterGraphs, childFilter.filterGraphId).rootNodeId,
      });
      return this.and(negatedChildFilter);
    }

    return new BuiltFilterBuilder(
      this.sourceType,
      withNewRootNode(this.filter, {
        filterType: "NOT",
        targetType: this.sourceType,
        childNodeId: requireGraph(this.filter.filterGraphs, this.filter.filterGraphId).rootNodeId,
      }),
    );
  }

  build(): BuiltDocFilter<S> {
    return this.filter;
  }
}

export class SubfilterBuilder<S extends FilterEntitySourceId> {
  constructor(
    private readonly sourceType: S,
    private readonly filterGraph?: BuiltDocFilter<S>,
  ) {}

  where(filter: FilterInput<S>): SubfilterBuilder<S> {
    return new SubfilterBuilder(this.sourceType, resolveFilter(filter));
  }

  selectPaths(...paths: readonly [PropertyPath<S>, ...PropertyPath<S>[]]): BuiltSubfilterValueSource<S> {
    return {
      sourceType: this.sourceType,
      filterGraphId: this.filterGraph?.filterGraphId,
      filterGraphs: this.filterGraph?.filterGraphs ?? {},
      selectPaths: paths,
    };
  }

  selectPathsArray(paths: readonly PropertyPath<S>[]): BuiltSubfilterValueSource<S> {
    return {
      sourceType: this.sourceType,
      filterGraphId: this.filterGraph?.filterGraphId,
      filterGraphs: this.filterGraph?.filterGraphs ?? {},
      selectPaths: paths,
    };
  }
}

export class SourceFilterBuilder<S extends FilterEntitySourceId> {
  constructor(private readonly sourceType: S) {}

  all(): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(
      this.sourceType,
      createSingleNodeFilter(this.sourceType, {
        filterType: "AND",
        targetType: this.sourceType,
        childrenNodeIds: [],
      }),
    );
  }

  eq(path: PropertyPath<S>, value: FilterLiteralValue): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "EQ", path, [value]));
  }

  neq(path: PropertyPath<S>, value: FilterLiteralValue): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "NEQ", path, [value]));
  }

  in(path: PropertyPath<S>, values: readonly FilterLiteralValue[]): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "IN", path, values));
  }

  inFrom<VS extends FilterEntitySourceId>(
    path: PropertyPath<S>,
    valueSource: BuiltSubfilterValueSource<VS>,
  ): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(
      this.sourceType,
      predicateFilter(this.sourceType, "IN", path, undefined, valueSource),
    );
  }

  contains(path: PropertyPath<S>, value: FilterLiteralValue): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "CONTAINS", path, [value]));
  }

  containsFrom(
    path: PropertyPath<S>,
    valueSource: BuiltSubfilterValueSource<FilterEntitySourceId>,
  ): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(
      this.sourceType,
      predicateFilter(this.sourceType, "CONTAINS", path, undefined, valueSource),
    );
  }

  intersects(path: PropertyPath<S>, values: readonly FilterLiteralValue[]): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "INTERSECTS", path, values));
  }

  intersectsFrom(
    path: PropertyPath<S>,
    valueSource: BuiltSubfilterValueSource<FilterEntitySourceId>,
  ): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(
      this.sourceType,
      predicateFilter(this.sourceType, "INTERSECTS", path, undefined, valueSource),
    );
  }

  exists(path: PropertyPath<S>): BuiltFilterBuilder<S> {
    return new BuiltFilterBuilder(this.sourceType, predicateFilter(this.sourceType, "EXISTS", path));
  }

  and(...children: readonly FilterInput<S>[]): BuiltFilterBuilder<S> {
    const resolvedChildren = children.map((child) => resolveFilter(child));
    return new BuiltFilterBuilder(
      this.sourceType,
      combineChildrenWithConnective(this.sourceType, "AND", resolvedChildren),
    );
  }

  or(...children: readonly FilterInput<S>[]): BuiltFilterBuilder<S> {
    const resolvedChildren = children.map((child) => resolveFilter(child));
    return new BuiltFilterBuilder(
      this.sourceType,
      combineChildrenWithConnective(this.sourceType, "OR", resolvedChildren),
    );
  }

  not(child: FilterInput<S>): BuiltFilterBuilder<S> {
    const resolvedChild = resolveFilter(child);
    assertSameSourceType(this.sourceType, resolvedChild);
    const negatedChild = withNewRootNode(resolvedChild, {
      filterType: "NOT",
      targetType: this.sourceType,
      childNodeId: requireGraph(resolvedChild.filterGraphs, resolvedChild.filterGraphId).rootNodeId,
    });
    return new BuiltFilterBuilder(this.sourceType, negatedChild);
  }
}

export const bgf = {
  from<S extends FilterEntitySourceId>(sourceType: S): SourceFilterBuilder<S> {
    return new SourceFilterBuilder(sourceType);
  },
  subfilter<S extends FilterEntitySourceId>(sourceType: S): SubfilterBuilder<S> {
    return new SubfilterBuilder(sourceType);
  },
};
